import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { flattenToAppURL } from "@plone/volto/helpers";
import { Link } from "react-router-dom";
import moment from "moment";
import { useIntl } from "react-intl";
import { getEventCard, getEventDate, getEventTime } from "./sharedUtils";
import processItemsForRecurrence from "./processItemsForRecurrence";
import RenderImage from "./renderImage";

const CommonItemRenderer = ({
  items,
  showRecurrence,
  quote,
  showTitle = true,
  eventCard,
  titleTag: TitleTag = "h2",
  eventDate,
  eventTime,
  eventLocation,
  showDescription = true,
  truncateDescription = false,
  descriptionLength = 150,
  effectiveDate,
  expirationDate,
  isEditMode,
  imageSide = "up",
  imageWidth = 4,
  howManyColumns = 1,
  fetchPriority,
  slidesToScroll = 1,
  autoPlay = true,
  autoplaySpeed = 5,
  creatorauthor,
}) => {
  const intl = useIntl();

  const resolveImageField = (item, url) => {
    if (!item.image_field) return { url, image_field: item.image_field };
    // image_scales is included in Plone listing brains.
    // For relation fields (e.g. preview_image_link) the entry contains a
    // base_path pointing to the linked object; for embedded fields it does not.
    const scaleEntry = item.image_scales?.[item.image_field];
    if (scaleEntry) {
      const entry = Array.isArray(scaleEntry) ? scaleEntry[0] : scaleEntry;
      if (entry?.base_path) {
        return {
          url: flattenToAppURL(entry.base_path),
          image_field: 'image',
        };
      }
    }
    return { url, image_field: item.image_field };
  };

  const processedItems = useMemo(() => {
    if (showRecurrence) {
      return processItemsForRecurrence(items);
    }
    return items.map((item) => {
      const url = flattenToAppURL(item["@id"]);
      const { url: imageUrl, image_field } = resolveImageField(item, url);
      return {
        ...item,
        url,
        image_field,
        _imageUrl: imageUrl,
      };
    });
  }, [items, showRecurrence]);

  const renderMetadata = useMemo(() => {
    return (item) => {
      const eventTimeValue = getEventTime(item); // null when whole_day is true
      return (
        <>
          {eventDate && (
            <span className="start-date">{getEventDate(item)}</span>
          )}
          {eventTime && eventDate && eventTimeValue && <span> | </span>}
          {eventTime && eventTimeValue && (
            <span className="start-time">{eventTimeValue}</span>
          )}
        </>
      );
    };
  }, [eventDate, eventTime]);

  const renderContent = (item) => (
    <>
      {quote && (
        <>
          <blockquote>{item.description}</blockquote>
          <div className="styled-slate right has--align--right align styled">
            {item.title || item.id}
          </div>
        </>
      )}
      {eventCard && getEventCard(item)}
      {showTitle && (
        <TitleTag>
          {imageSide === "background" ? (
            item.title || item.id
          ) : !isEditMode ? (
            <Link to={item.url}>{item.title ? item.title : item.id}</Link>
          ) : (
            item.title || item.id
          )}
        </TitleTag>
      )}
      <div className="item-meta">
        {creatorauthor && item.Creator && (
          <span className="author">{item.Creator}</span>
        )}
        {(eventDate || eventTime || eventLocation) && (
          <span className="event-meta">
            {(eventDate || eventTime) && (
              <span className="advancedDatetime">{renderMetadata(item)}</span>
            )}
            {eventDate && eventLocation && item.location && <br />}
            {eventLocation && item.location && (
              <span className="location">{item.location}</span>
            )}
          </span>
        )}
        {effectiveDate && item.effective && (
          <span className="effectiveDate">
            {moment(item.effective).format("D MMM YYYY")}
          </span>
        )}
        {expirationDate && item.expires && (
          <span className="expirationDate">
            Expires: {moment(item.expires).format("D MMM YYYY")}
          </span>
        )}
      </div>
      {showDescription && item.description && (
        <p>
          {truncateDescription && item.description.length > descriptionLength
            ? `${item.description.slice(0, descriptionLength).trimEnd()}…`
            : item.description}
        </p>
      )}
    </>
  );

  // imageWidth is 2–6 out of 12; convert to a percentage for flex-basis
  const imagePercent = `${Math.round((imageWidth / 12) * 100)}%`;
  const contentPercent = `${Math.round(((12 - imageWidth) / 12) * 100)}%`;

  return (
    <div className="fade-in loaded">
      {processedItems.map((item) => (
        <div
          key={item["@id"]}
          className={`advanced-item advanced-item--${imageSide}`}
        >
          {imageSide === "background" ? (
            <div className="backgroundimage">
              <Link to={item.url} condition={!isEditMode}>
                <div className="focuspoint">
                  <img
                    sizes="(min-width: 768px) and (max-width: 991px) 171px, (min-width: 992px) and (max-width: 1199px) 223px, (min-width: 1200px) 272px"
                    srcSet={`
                      ${item._imageUrl || item.url}/@@images/${item.image_field}/mini 200w,
                      ${item._imageUrl || item.url}/@@images/${item.image_field}/preview 400w,
                      ${item._imageUrl || item.url}/@@images/${item.image_field}/teaser 600w,
                      ${item._imageUrl || item.url}/@@images/${item.image_field}/large 800w,
                      ${item._imageUrl || item.url}/@@images/${item.image_field}/larger 1000w,
                      ${item._imageUrl || item.url}/@@images/${item.image_field}/great 1200w,
                      ${item._imageUrl || item.url}/@@images/${item.image_field}/huge 1600w
                    `}
                    src={`${item._imageUrl || item.url}/@@images/${item.image_field}/preview`}
                    alt={item.title || ""}
                    className="listImage"
                    loading={fetchPriority === "high" ? "eager" : "lazy"}
                    fetchpriority={fetchPriority}
                    width="100%"
                    height="auto"
                  />
                </div>
                <div className="info-text">{renderContent(item)}</div>
              </Link>
            </div>
          ) : (
            <div
              className="advanced-item__inner"
              style={{
                "--image-basis": imagePercent,
                "--content-basis": contentPercent,
              }}
            >
              {["up", "left"].includes(imageSide) && item.image_field && (
                <div className="advanced-item__image">
                  <Link to={item.url}>
                    <RenderImage
                      item={item}
                      isEditMode={isEditMode}
                      howManyColumns={howManyColumns}
                      fetchPriority={fetchPriority}
                    />
                  </Link>
                </div>
              )}
              <div className="advanced-item__content">
                {renderContent(item)}
              </div>
              {["right", "down"].includes(imageSide) && item.image_field && (
                <div className="advanced-item__image">
                  <Link to={item.url}>
                    <RenderImage
                      item={item}
                      isEditMode={isEditMode}
                      howManyColumns={howManyColumns}
                      fetchPriority={fetchPriority}
                    />
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

CommonItemRenderer.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  showRecurrence: PropTypes.bool,
  quote: PropTypes.bool,
  showTitle: PropTypes.bool,
  eventCard: PropTypes.bool,
  titleTag: PropTypes.string,
  eventDate: PropTypes.bool,
  eventTime: PropTypes.bool,
  eventLocation: PropTypes.bool,
  showDescription: PropTypes.bool,
  truncateDescription: PropTypes.bool,
  descriptionLength: PropTypes.number,
  effectiveDate: PropTypes.bool,
  expirationDate: PropTypes.bool,
  isEditMode: PropTypes.bool,
  imageSide: PropTypes.string,
  imageWidth: PropTypes.number,
  howManyColumns: PropTypes.number,
  fetchPriority: PropTypes.string,
  slidesToScroll: PropTypes.number,
  autoPlay: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
  creatorauthor: PropTypes.bool,
};

export default CommonItemRenderer;
